import type { TableBrowserThroughput } from '.';
import { effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { merge } from 'rxjs';
import { bufferTime, map } from 'rxjs/operators';
import type { ReactBindings } from 'src/types/bindings';

/** Counters average over this window, as the legacy footer did. */
export const THROUGHPUT_WINDOW_MS = 5000;

/**
 * Proxy-wide counters for the footer. Continuous live data arrives as diffs on `diffs$` while full
 * snapshots arrive on `updates$`; both are counted so the numbers reflect activity regardless of
 * which stream the active transport uses.
 */
export class ThroughputModelImpl implements TableBrowserThroughput, Disposable {
    readonly #disposableStack = new DisposableStack();
    readonly #updatesPerSecond = signal(0);
    readonly #rowsPerSecond = signal(0);
    readonly #requestLagMs = signal(0);

    constructor(proxyClientService: ReactBindings['ProxyClientService']) {
        const target = proxyClientService.target;
        this.#disposableStack.defer(
            effect(() => {
                const { client } = target.value;
                // Other subscriptions on this proxy can deliver updates before this page requests
                // anything; lag is only meaningful once a request has been observed.
                let requestInstant: number | null = null;
                const subscriptions = [
                    client.dataRequest$.subscribe(() => {
                        requestInstant = Date.now();
                    }),
                    client.updates$.subscribe(() => {
                        if (requestInstant !== null) {
                            this.#requestLagMs.value = Date.now() - requestInstant;
                        }
                    }),
                    merge(client.updates$, client.diffs$)
                        .pipe(
                            map((batch) => Iterator.from(batch.updates).reduce((count) => count + 1, 0)),
                            bufferTime(THROUGHPUT_WINDOW_MS),
                        )
                        .subscribe((rowCounts) => {
                            const seconds = THROUGHPUT_WINDOW_MS / 1000;
                            this.#updatesPerSecond.value = rowCounts.length / seconds;
                            this.#rowsPerSecond.value = rowCounts.reduce((sum, count) => sum + count, 0) / seconds;
                        }),
                ];
                return () => {
                    for (const subscription of subscriptions) {
                        subscription.unsubscribe();
                    }
                };
            }),
        );
    }

    get updatesPerSecond(): ReadonlySignal<number> {
        return this.#updatesPerSecond;
    }

    get rowsPerSecond(): ReadonlySignal<number> {
        return this.#rowsPerSecond;
    }

    get requestLagMs(): ReadonlySignal<number> {
        return this.#requestLagMs;
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
